import { toast } from "sonner";

import { HttpApiService } from "@/shared/services/httpApi.service";

type ToastOptions = {
  /** Secondary text rendered below the title. Use for extra context or action details. */
  description?: string;
  /**
   * How long the toast stays visible in ms. Defaults: success/info/warning = 4000, error = 6000. Override for
   * operations where the user needs more reading time.
   */
  duration?: number;
  /** Inline action button. Use for a single recoverable action (e.g. "Undo", "Retry"). */
  action?: { label: string; onClick: () => void };
  /** Pass an `id` to update or dismiss a specific toast programmatically. */
  id?: string | number;
};

type PromiseMessages<T> = {
  loading: string;
  success: string | ((data: T) => string);
  error: string | ((err: unknown) => string);
};

/**
 * Centralised toast utility for the Coloca design system.
 *
 * Always import from this module — never call `toast()` from `"sonner"` directly. This ensures consistent
 * defaults (duration, position) and a single point of control.
 *
 * Requires `<ColocaToaster />` to be mounted in the app root (already in `App.tsx`).
 */
export const colocaToast = {
  success: (message: string, options?: ToastOptions) =>
    toast.success(message, { duration: 4000, ...options }),

  error: (message: string, options?: ToastOptions) => toast.error(message, { duration: 6000, ...options }),

  info: (message: string, options?: ToastOptions) => toast.info(message, { duration: 4000, ...options }),

  warning: (message: string, options?: ToastOptions) =>
    toast.warning(message, { duration: 5000, ...options }),

  default: (message: string, options?: ToastOptions) =>
    toast.message(message, { duration: 4000, ...options }),

  /**
   * Shows a loading toast while `promise` is pending, then transitions to success or error. Use for async
   * operations (API calls, file uploads) where you want automatic feedback without wiring success/error state
   * manually.
   *
   * Returns the toast `id` — pass it to `dismiss()` if you need to cancel early.
   */
  promise: <T>(promise: Promise<T>, messages: PromiseMessages<T>) => toast.promise(promise, messages),

  /**
   * Shows a persistent loading indicator. Returns a toast `id` — always call `dismiss(id)` or replace with
   * `success()`/`error()` when the operation completes.
   */
  loading: (message: string, options?: Omit<ToastOptions, "duration">) =>
    toast.loading(message, { ...options }),

  /** Dismiss a specific toast by `id`, or all toasts when called with no arguments. */
  dismiss: (id?: string | number) => toast.dismiss(id),
};

/**
 * Shows an error toast for a caught API/unknown error, normalizing it through
 * `HttpApiService.getErrorMessage`. Shorthand for the `colocaToast.error(HttpApiService.getErrorMessage(e))`
 * idiom repeated across the app — use directly as a `.catch` handler: `promise.catch(toastApiError)`.
 */
export function toastApiError(error: unknown): void {
  colocaToast.error(HttpApiService.getErrorMessage(error));
}
